<template>
    <div class="ap-table-card">
        <div class="table-responsive">
            <table class="table ap-table">
                <thead>
                <tr>
                    <th>Master Budget Line</th>
                    <th>Budget Allocation</th>
                    <th>Version</th>
                    <th class="text-end">Amount</th>
                    <th class="text-end">Change vs Previous</th>
                    <th>Submitted</th>
                    <th v-if="showDecision">{{ decisionLabel }}</th>
                    <th class="text-center">Status</th>
                    <th class="text-end">Action</th>
                </tr>
                </thead>
                <tbody v-if="loading">
                <TableSkeleton :rows="6" :columns="columnCount"/>
                </tbody>
                <tbody v-if="! loading">
                <tr v-for="item in rows" :key="item.id">
                    <td>
                        <div class="ap-strong">{{ item.budget_line_name }}</div>
                        <div v-if="item.budget_line_code" class="ap-sub">{{ item.budget_line_code }}</div>
                    </td>
                    <td>
                        <div class="ap-strong">{{ item.budget_name }}</div>
                        <div v-if="item.budget_code" class="ap-sub">{{ item.budget_code }}</div>
                    </td>
                    <td>
                        <span class="ap-version">V{{ item.version_no }}</span>
                        <div class="ap-sub">{{ budgetVersionKind(item.version_no) }}</div>
                    </td>
                    <td class="text-end text-nowrap">{{ commaFormat(item.total_amount) }}</td>
                    <td class="text-end text-nowrap" :class="changeClass(item)">{{ changeLabel(item) }}</td>
                    <td>
                        <div>{{ formatDateTimeLabel(item.submitted_at) }}</div>
                        <div class="ap-sub">{{ item.submitted_by_name }}</div>
                    </td>
                    <td v-if="showDecision">
                        <div>{{ formatDateTimeLabel(decisionDate(item)) }}</div>
                        <div class="ap-sub">{{ decisionBy(item) }}</div>
                        <div v-if="item.note && item.status === 'rejected'" class="ap-note">{{ item.note }}</div>
                    </td>
                    <td class="text-center">
                        <StatusChip :status="item.status" :tones="budgetVersionTones"/>
                    </td>
                    <td class="text-end">
                        <button type="button" class="ap-action-btn" @click="emit('review', item)">
                            <i class="fa-regular fa-eye"></i>
                            <span>Review</span>
                        </button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <EmptyState
            v-if="! loading && ! rows.length"
            icon="fa-solid fa-clipboard-check"
            :title="emptyTitle"
            message="Budget versions appear here once they are submitted for approval."
        />
    </div>
</template>

<script setup>
import { computed } from 'vue'
import StatusChip from '@/components/atom/StatusChip.vue'
import EmptyState from '@/components/atom/EmptyState.vue'
import TableSkeleton from '@/components/atom/TableSkeleton.vue'
import figureFormatter from '@/services/utils/figureFormatter'
import { budgetVersionKind, budgetVersionTones, formatDateTimeLabel } from '@/services/utils/global.js'

const props = defineProps({
    rows: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    },
    status: {
        type: String,
        default: 'pending'
    }
})

const emit = defineEmits(['review'])

const { commaFormat } = figureFormatter()

const showDecision = computed(() => props.status !== 'pending')

const decisionLabel = computed(() => props.status === 'rejected' ? 'Rejected' : 'Approved')

const columnCount = computed(() => showDecision.value ? 9 : 8)

const emptyTitle = computed(() => {
    if (props.status === 'approved') return 'No approved versions'
    if (props.status === 'rejected') return 'No rejected versions'
    return 'Nothing waiting for approval'
})

const changeLabel = (item) => {
    if (item.previous_total_amount === null) return 'New budget'
    const change = Number(item.total_amount || 0) - Number(item.previous_total_amount || 0)
    if (! change) return 'No change'
    const sign = change > 0 ? '+' : '-'
    return sign + commaFormat(Math.abs(change))
}

const changeClass = (item) => {
    if (item.previous_total_amount === null) return 'ap-sub'
    const change = Number(item.total_amount || 0) - Number(item.previous_total_amount || 0)
    if (change > 0) return 'ap-up'
    if (change < 0) return 'ap-down'
    return 'ap-sub'
}

const decisionDate = (item) => item.status === 'rejected' ? item.updated_at : item.approved_at

const decisionBy = (item) => item.status === 'rejected' ? item.decided_by_name : item.approved_by_name
</script>

<style scoped>
.ap-table-card {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    overflow: hidden;
}
.ap-table {
    min-width: 980px;
    margin-bottom: 0;
}
.ap-table thead th {
    padding: 12px 14px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #475467;
    white-space: nowrap;
}
.ap-table tbody td {
    padding: 14px;
    border-bottom: 1px solid #f2f4f7;
    font-size: 14px;
    line-height: 1.45;
    color: #344054;
    vertical-align: middle;
}
.ap-table tbody tr:last-child td {
    border-bottom: 0;
}
.ap-table tbody tr:hover {
    background: #fcfcfd;
}
.ap-strong {
    font-weight: 600;
    color: #101828;
}
.ap-sub {
    font-size: 13px;
    color: #667085;
}
.ap-version {
    font-weight: 700;
    color: #101828;
}
.ap-up {
    color: #b54708;
    font-weight: 600;
}
.ap-down {
    color: #067647;
    font-weight: 600;
}
.ap-note {
    margin-top: 2px;
    font-size: 13px;
    color: #b42318;
    white-space: pre-wrap;
}
.ap-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid #e4e7ec;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    color: #475467;
    transition: all .15s ease;
}
.ap-action-btn:hover {
    border-color: #7367f0;
    background: #f4f3ff;
    color: #7367f0;
}
</style>
